import { APP_CONFIG } from "../helpers/appHelpers";
import { PaginatorInfo } from "./generic.dto";

export type Payslips = {
	id: number;
	companyId: number;
	employeeId: number;
	payPeriodId: number;
	basicPayAmount: number;
	basicPayPerPayPeriod: number;
	allowanceAmount: number;
	overtimeAmount: number;
	bonusAmount: number;
	grossAmount: number;
	employeePensionAmount: number;
	employerPensionAmount: number;
	employeeSavingSchemeAmount: number;
	employerSavingSchemeAmount: number;
	backpayAmount: number;
	partialSalaryAmount: number;
	salaryAdvanceAmount: number;
	unionDeductionsAmount: number;
	loanRepaymentAmount: number;
	taxAmount: number;
	netPayAmount: number;
	employee: EmployeeDao;
	salaryItems: SalaryItemDto[];
	payPeriod: PayPeriod;
};

export type SalaryItemDto = {
	id: number;
	name: string;
	type: string;
	amount: number;
	balance: number;
};

type EmployeeDao = {
	id: number;
	title: string;
	firstName: string;
	lastName: string;
	otherNames: string;
	companyId: number;
	notchId: number;
	bandId: number;
	status: string;
	ssn: string;
	taxIdentificationNumber: string;
	majorGradeLevelId: number;
	minorGradeLevelId: number;
	employmentDate: string;
	terminationDate: string;
};

export type PayrollPeriodDto = {
	payPeriodId: number;
	companyId: number;
	initiatedBy: number;
	considerEmployeesWithExpiredContract: boolean;
	basic: boolean;
	considerAllEmployees: boolean;
	applicableAllowanceIds: number[];
	applicableBenefitIds: number[];
	applicableEmployeeIds: number[];
	applicableEmployeeBandIds: number[];
};

export type PayrollRuns = {
	id: number;
	payPeriodId: number;
	payPeriod: PayPeriod;
	companyId: number;
	totalCost: number;
	initiatedBy: number;
	createdAt: string;
	numberOfEmployees: number;
	processedEmployees: number;
	status: PayrollRunStatus;
	isApproved: boolean;
	convertForeignCurrency: boolean;
};

export enum PayrollRunStatus {
	SUBMITTED = "SUBMITTED",
	PROCESSING = "PROCESSING",
	COMPLETED = "COMPLETED",
	ERROR = "ERROR",
}

export class CreatePayrollDto {
	payPeriodId!: number;
	companyId!: number;
	considerEmployeesWithExpiredContracts!: boolean;
	considerAllComponents!: boolean;
	basic!: boolean;
	considerAllEmployees!: boolean;
	convertForeignCurrency!: boolean;
	applicableAllowanceIds!: number[];
	applicableBenefitIds!: number[];
	applicableEmployeeIds!: number[];
	applicableEmployeeBandIds!: number[];
	applicableBonusIds!: number[];
}

export class QueryParam {
	adminId?: number;
}

export class CreatePayrollObject {
	payrollRunId!: number;
}

export type PayPeriod = {
	id: number;
	organizationId: string;
	companyId: number;
	code: string;
	year: string;
	taxCodeId: number;
	startDate: string;
	endDate: string;
	sequenceNumber: number;
	timePeriod: string;
	daysInPayPeriod: number;
};

export class QueryPayrollDto {
	company_id?: number;
	adminId?: number;

	page?: number = 1;
	limit?: number;
}

export class QueryCompanyPayslipsDto {
	adminId?: number;

	page?: number = 1;
	limit?: number;
}

export class SearchCompanyPayslipsDto {
	q?: string;
	page?: number = 1;
	limit?: number = APP_CONFIG.PAGE_LIMIT;
}

export class QueryEmployeePayslipsDto {
	adminId?: number;
	employeeId?: number;
	payPeriodId?: number;
}

export type PayrollRunStatusObject = {
	processed: number;
	total: number;
};

export type MigrationDto = {
	timestamp: Date;
	operation: string;
	exception: string;
	licenseFailed: boolean;
	initialSchemaVersion: string;
	targetSchemaVersion: string;
	schemaName: string;
	migrations: migration[];
	migrationsExecuted: number;
	success: boolean;
	flywayVersion: string;
	database: string;
	warnings: string[];
	totalMigrationTime: number;
};

type migration = {
	category: string;
	version: string;
	description: string;
	type: string;
	filepath: string;
	executionTime: number;
};
export class QueryReportDto {
	adminId?: number;
	companyId!: number;
	payPeriodId!: number;
	page?: number;
	limit?: number;
}

export type SSFResponseDto = {
	id: string;
	name: string;
	ssn: string;
	basicSalary: number;
	employeeContribution: number;
	employerContribution: number;
	total: number;
	tierAmount: number;
};

export type SavingSchemeReportResponseDto = {
	id: string;
	name: string;
	employeeContribution: number;
	accountCode: string;
	branchCode: string;
};

export type BankReportResponseDto = {
	BANK_CODE: string;
	BANK_NAME: string;
	EMPLOYEE_CODE: string;
	NET: string;
	EMPLOYEE_NAME: string;
	ACCOUNT_NUMBER: string;
	BANK_BRANCH_CODE: string;
	BANK_BRANCH_NAME: string;
};

export class DownloadReportQueryDto {
	adminId?: number;
	type!: ReportType;
	companyId!: number;
	payPeriodId!: number;
	ssfName?: string;
}

export type SalaryReportResponseDto = {
	employeeCode: string;
	employeeName: string;
	basicPayAmount: number;
	allowanceAmount: number;
	bonusAmount: number;
	grossAmount: number;
	ssfEmployeeAmount: number;
	taxableAmount: number;
	incomeTaxAmount: number;
	savingSchemesAmount: number;
	otherDeductionsAmount: number;
	loansAmount: number;
	explicitDeductionAmount: number;
	totalDeductionsAmount: number;
	netAmount: number;
};

export enum ReportType {
	SAVING_SCHEME = "SAVING_SCHEME",
	SSF = "SSF",
	BANK_REPORT = "BANK_REPORT",
	TAX_REPORT = "TAX_REPORT",
	PAYROLL_SUMMARY = "PAYROLL_SUMMARY",
	SALARY_ADVANCE = "SALARY_ADVANCE",
	SALARY_REPORT = "SALARY_REPORT",
	PAYE_SUMMARY = "PAYE_SUMMARY",
	EMPLOYEE_ANNUAL_TAX_DEDUCTION = "EMPLOYEE_ANNUAL_TAX_DEDUCTION",
}

export class TaxReportResponseDto {
	employeeName?: string;
	position?: string;
	residencyType?: string;
	basicSalaryAmount?: number;
	isSecondaryEmployment?: boolean;
	paidSSNIT?: boolean;
	socialSecurityAmount?: number;
	thirdTier?: number;
	allowanceAmount?: number;
	bonusAmount?: number;
	bonusTaxAmount?: number;
	excessBonusAmount?: number;
	totalCashEmolument?: number;
	benefitInKindAmount?: number;
	totalAssessableIncomeAmount?: number;
	totalReliefAmount?: number;
	chargeableIncomeAmount?: number;
	taxDeductibleAmount?: number;
	overtimeIncomeAmount?: number;
	overtimeTaxAmount?: number;
	totalTaxPayableAmount?: number;
}

export type PayrollSummaryItemDto = {
	particulars: string;
	debit: number | null;
	credit: number | null;
};

export type PayrollSummaryResponseDto = {
	summaryItems: PayrollSummaryItemDto[];
};

export type PayrollSummaryApiResponse = {
	data: PayrollSummaryResponseDto;
};

export type FileDownloadLink = {
	data: string;
};

export class SearchReportDto {
	adminId?: number;
	q!: string;
	payPeriodId?: number;
}

export type AdvanceItemDto = {
	employeeCode: string;
	employeeName: string;
	advanceAmount: number;
};

export type SalaryAdvanceReportResponseDto = {
	advancesByStation: {
		[stationName: string]: AdvanceItemDto[];
	};
	totalAdvanceAmount: number;
	totalEmployeesWithAdvances: number;
};

export type PayeSummaryResponseDto = {
	particulars: string;
	amount: number;
};

export type PayeSummaryItemDto = {
	particulars: string;
	amount: number;
};

export type PayeSummaryApiResponse = {
	data: PayeSummaryItemDto[];
};

export type PayeSummaryCategoryDto = {
	categoryName: string;
	numberOfStaff: number;
	totalEmolument: number;
	taxDeducted: number;
};

export type PayeSummaryDataDto = {
	companyName: string;
	employerTin: string;
	taxOffice: string;
	month: string;
	categories: PayeSummaryCategoryDto[];
	totalStaff: number;
	totalEmolument: number;
	totalTaxDeducted: number;
};

export type PayeSummaryPaginator = {
	data: PayeSummaryDataDto;
};

export type EmployeeAnnualTaxDeductionMonthlyRowDto = {
	month: string;
	consolidatedBasicSalary: number;
	cashAllowanceNotConsolidated: number;
	benefitInKind: number;
	grossRemuneration: number;
	deductibleRelief: number;
	socialSecurityRelief: number;
	taxableEmolument: number;
	taxDeducted: number;
};

export type EmployeeAnnualTaxDeductionResponseDto = {
	employeeName: string;
	rankOrGrade: string;
	employeeAddress: string;
	staffTin: string;
	ghanaCardNo: string;
	socialSecurityNo: string;
	employerName: string;
	employerTin: string;
	employerAddress: string;
	yearOfAssessment: string;
	monthlyRows: EmployeeAnnualTaxDeductionMonthlyRowDto[];
	totalConsolidatedBasicSalary: number;
	totalCashAllowanceNotConsolidated: number;
	totalBenefitInKind: number;
	totalGrossRemuneration: number;
	totalDeductibleRelief: number;
	totalSocialSecurityRelief: number;
	totalTaxableEmolument: number;
	totalTaxDeducted: number;
};

export interface PayeSummaryReportPaginator extends PaginatorInfo<PayeSummaryResponseDto> {}
export interface PayslipPaginator extends PaginatorInfo<Payslips> {}
export interface PayrollRunsPaginator extends PaginatorInfo<PayrollRuns> {}
export interface SSFResponsePaginator extends PaginatorInfo<SSFResponseDto> {}
export interface SavingSchemeReportResponsePaginator extends PaginatorInfo<SavingSchemeReportResponseDto> {}
export interface DownloadReportPaginator extends PaginatorInfo<String> {}
export interface SalaryReportPaginator extends PaginatorInfo<SalaryReportResponseDto> {}
export interface BankReportPaginator extends PaginatorInfo<BankReportResponseDto> {}
export interface TaxReportPaginator extends PaginatorInfo<TaxReportResponseDto> {}
export interface SalaryAdvanceReportPaginator extends PaginatorInfo<SalaryAdvanceReportResponseDto> {}
